import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, Chip } from '@mui/material';

// ── Relationship colors ──
const REL_STYLES = {
  '仲間':   { color: '#00e676', glow: '#00e67640' },
  '師弟':   { color: '#40c4ff', glow: '#40c4ff40' },
  '主従':   { color: '#448aff', glow: '#448aff40' },
  '家族':   { color: '#ffab40', glow: '#ffab4040' },
  '恋愛':   { color: '#ff80ab', glow: '#ff80ab40' },
  '敵対':   { color: '#ff5252', glow: '#ff525240' },
  'ライバル': { color: '#ff6e40', glow: '#ff6e4040' },
};
const getRelStyle = (type) => {
  for (const [k, v] of Object.entries(REL_STYLES)) {
    if (type.includes(k)) return v;
  }
  return { color: '#82b1ff', glow: '#82b1ff40' };
};

// ── Sample data: a fantasy story ──
const DEMO_CHARACTERS = [
  {
    name: '蒼月 凛',
    description: '剣術に長けた少女。失われた王国の末裔。',
    confidence: 0.95,
    aliases: ['凛'],
    relationships: [
      { target: '黒崎 悠真', type: '仲間' },
      { target: '白銀 ミレイ', type: '師弟' },
      { target: '影宮 零', type: '敵対' },
      { target: '天城 紅葉', type: '仲間' },
    ],
    changes: ['覚醒', '決意'],
  },
  {
    name: '黒崎 悠真',
    description: '凛の幼なじみ。闇の力に目覚める。',
    confidence: 0.9,
    aliases: ['悠真'],
    relationships: [
      { target: '蒼月 凛', type: '仲間' },
      { target: '影宮 零', type: 'ライバル' },
      { target: '灰原 楓', type: '恋愛' },
    ],
    changes: ['葛藤'],
  },
  {
    name: '白銀 ミレイ',
    description: '伝説の魔導師。凛の師匠。',
    confidence: 0.85,
    aliases: ['ミレイ'],
    relationships: [
      { target: '蒼月 凛', type: '師弟' },
      { target: '影宮 零', type: '敵対' },
      { target: '天城 紅葉', type: '仲間' },
    ],
    changes: [],
  },
  {
    name: '影宮 零',
    description: '漆黒の騎士。かつての王国を滅ぼした男。',
    confidence: 0.92,
    aliases: ['零'],
    relationships: [
      { target: '蒼月 凛', type: '敵対' },
      { target: '黒崎 悠真', type: 'ライバル' },
      { target: '白銀 ミレイ', type: '敵対' },
    ],
    changes: ['暴走', '真実'],
  },
  {
    name: '天城 紅葉',
    description: '弓の名手。森の民の族長の娘。',
    confidence: 0.78,
    aliases: ['紅葉'],
    relationships: [
      { target: '蒼月 凛', type: '仲間' },
      { target: '白銀 ミレイ', type: '仲間' },
      { target: '灰原 楓', type: '家族' },
    ],
    changes: [],
  },
  {
    name: '灰原 楓',
    description: '薬師。紅葉の妹。',
    confidence: 0.72,
    aliases: ['楓'],
    relationships: [
      { target: '黒崎 悠真', type: '恋愛' },
      { target: '天城 紅葉', type: '家族' },
    ],
    changes: [],
  },
  {
    name: '龍堂 雷牙',
    description: '傭兵団の団長。義理堅い男。',
    confidence: 0.68,
    aliases: ['雷牙'],
    relationships: [
      { target: '黒崎 悠真', type: '主従' },
      { target: '蒼月 凛', type: '仲間' },
    ],
    changes: [],
  },
];

// ── Force layout ──
function forceLayout(nodes, edges, width, height) {
  const n = nodes.length;
  const positioned = nodes.map((nd, i) => ({
    ...nd,
    x: width / 2 + Math.cos((i * 2 * Math.PI) / n) * Math.min(width, height) * 0.3,
    y: height / 2 + Math.sin((i * 2 * Math.PI) / n) * Math.min(width, height) * 0.3,
    vx: 0,
    vy: 0,
  }));
  const k = Math.sqrt((width * height) / n) * 0.7;
  const iterations = 200;

  for (let iter = 0; iter < iterations; iter++) {
    const cooling = 1 - iter / iterations;
    // Repulsion
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let dx = positioned[j].x - positioned[i].x;
        let dy = positioned[j].y - positioned[i].y;
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
        const force = ((k * k) / dist) * cooling * 0.6;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        positioned[i].vx -= fx;
        positioned[i].vy -= fy;
        positioned[j].vx += fx;
        positioned[j].vy += fy;
      }
    }
    // Attraction
    for (const e of edges) {
      const a = positioned[e.source];
      const b = positioned[e.target];
      let dx = b.x - a.x;
      let dy = b.y - a.y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const force = ((dist - k * 0.8) / k) * cooling * 0.25;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    }
    // Center gravity + update
    const maxSpeed = 8 * cooling + 1;
    for (const p of positioned) {
      p.vx += (width / 2 - p.x) * 0.008 * cooling;
      p.vy += (height / 2 - p.y) * 0.008 * cooling;
      const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      if (speed > maxSpeed) {
        p.vx = (p.vx / speed) * maxSpeed;
        p.vy = (p.vy / speed) * maxSpeed;
      }
      p.x += p.vx;
      p.y += p.vy;
      p.x = Math.max(60, Math.min(width - 60, p.x));
      p.y = Math.max(60, Math.min(height - 60, p.y));
      p.vx *= 0.75;
      p.vy *= 0.75;
    }
  }
  return positioned;
}

// ── Node colors ──
const getNodeColors = (node, isSelected, isHovered) => {
  if (isSelected) return { main: '#ffd740', ring: '#ffd74080' };
  if (isHovered) return { main: '#82b1ff', ring: '#82b1ff60' };
  const h = 220 + (node.confidence || 0.5) * 80;
  return { main: `hsl(${h}, 70%, 60%)`, ring: `hsla(${h}, 70%, 60%, 0.3)` };
};

const DemoCharacterGraph = () => {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 900, h: 500 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Build graph data
  const { nodes, edges } = useMemo(() => {
    const nameToIdx = {};
    DEMO_CHARACTERS.forEach((c, i) => {
      nameToIdx[c.name] = i;
      c.aliases?.forEach((a) => {
        nameToIdx[a] = i;
      });
    });

    const nodeList = DEMO_CHARACTERS.map((c, i) => ({
      id: i,
      name: c.name,
      description: c.description,
      confidence: c.confidence || 0.5,
      relCount: c.relationships.length,
    }));

    const edgeSet = new Set();
    const edgeList = [];
    DEMO_CHARACTERS.forEach((c, i) => {
      c.relationships?.forEach((r) => {
        let targetIdx = nameToIdx[r.target];
        if (targetIdx === undefined) return;
        const key = [Math.min(i, targetIdx), Math.max(i, targetIdx), r.type].join('-');
        if (edgeSet.has(key)) return;
        edgeSet.add(key);
        const style = getRelStyle(r.type);
        edgeList.push({ source: i, target: targetIdx, type: r.type, ...style });
      });
    });

    return { nodes: nodeList, edges: edgeList };
  }, []);

  // Layout
  const positioned = useMemo(() => {
    if (size.w < 10 || size.h < 10) return nodes;
    return forceLayout(nodes, edges, size.w, size.h);
  }, [nodes, edges, size]);

  const handleNodeClick = useCallback((i) => {
    setSelectedNode((prev) => (prev === i ? null : i));
  }, []);

  // Selected character info
  const selectedChar = selectedNode !== null ? DEMO_CHARACTERS[selectedNode] : null;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      <svg width={size.w} height={size.h} style={{ display: 'block' }}>
        <defs>
          <filter id="demo-edgeGlow">
            <feGaussianBlur stdDeviation="3" />
          </filter>
          <filter id="demo-nodeGlow">
            <feGaussianBlur stdDeviation="6" />
          </filter>
          <filter id="demo-nodeGlowStrong">
            <feGaussianBlur stdDeviation="10" />
          </filter>
          <radialGradient id="demo-bgGrad" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#161b22" />
            <stop offset="100%" stopColor="#0d1117" />
          </radialGradient>
          <pattern id="demo-gridSmall" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
          </pattern>
          <pattern id="demo-gridLarge" width="200" height="200" patternUnits="userSpaceOnUse">
            <rect width="200" height="200" fill="url(#demo-gridSmall)" />
            <path d="M 200 0 L 0 0 0 200" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
          </pattern>
        </defs>

        {/* Background */}
        <rect width={size.w} height={size.h} fill="url(#demo-bgGrad)" />
        <rect width={size.w} height={size.h} fill="url(#demo-gridLarge)" />

        {/* Edges */}
        {edges.map((e, i) => {
          const s = positioned[e.source];
          const t = positioned[e.target];
          // 座標が未確定 (レイアウト前 / コンテナ幅が一瞬 <10px) の間は描画しない。
          // 旧コードは存在チェックのみで s.x が undefined のまま `M${s.x}` を生成し
          // SVG path "Mundefined,undefined" のコンソールエラーを多発させていた。
          if (!s || !t || !Number.isFinite(s.x) || !Number.isFinite(s.y) || !Number.isFinite(t.x) || !Number.isFinite(t.y)) return null;
          const dx = t.x - s.x;
          const dy = t.y - s.y;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;
          const nx = -dy / len;
          const ny = dx / len;
          const off = 15;
          const mx = (s.x + t.x) / 2 + nx * off;
          const my = (s.y + t.y) / 2 + ny * off;
          const path = `M${s.x},${s.y} Q${mx},${my} ${t.x},${t.y}`;
          const isHighlighted =
            selectedNode === e.source ||
            selectedNode === e.target ||
            hoveredNode === e.source ||
            hoveredNode === e.target;
          const isEdgeHovered = hoveredEdge === i;
          const isEnemy = e.type.includes('敵') || e.type.includes('ライバル');

          return (
            <g key={`edge-${i}`}>
              {isHighlighted && (
                <path d={path} fill="none" stroke={e.glow} strokeWidth={6} filter="url(#demo-edgeGlow)" />
              )}
              <path
                d={path}
                fill="none"
                stroke={isHighlighted || isEdgeHovered ? e.color : `${e.color}50`}
                strokeWidth={isHighlighted ? 2.5 : 1.5}
                strokeDasharray={isEnemy ? '6 3' : 'none'}
                style={{ cursor: 'pointer', transition: 'stroke 0.2s, stroke-width 0.2s' }}
                onMouseEnter={() => setHoveredEdge(i)}
                onMouseLeave={() => setHoveredEdge(null)}
              />
              {(isHighlighted || isEdgeHovered) && (
                <g transform={`translate(${mx},${my})`}>
                  <rect
                    x={-(e.type.length * 6 + 8) / 2}
                    y={-10}
                    width={e.type.length * 6 + 8}
                    height={18}
                    rx={4}
                    fill="rgba(13,17,23,0.85)"
                    stroke={`${e.color}60`}
                    strokeWidth={0.5}
                  />
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={10}
                    fontWeight={500}
                    fill={e.color}
                    style={{ pointerEvents: 'none' }}
                  >
                    {e.type}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {positioned.map((node, i) => {
          const isSel = selectedNode === i;
          const isHov = hoveredNode === i;
          const colors = getNodeColors(node, isSel, isHov);
          const r = 18 + node.relCount * 3;
          const displayName = node.name.length > 5 ? node.name.slice(0, 5) + '…' : node.name;

          return (
            <g
              key={`node-${i}`}
              className="graph-node"
              transform={`translate(${node.x},${node.y})`}
              onClick={() => handleNodeClick(i)}
              onMouseEnter={() => setHoveredNode(i)}
              onMouseLeave={() => setHoveredNode(null)}
              style={{ cursor: 'pointer' }}
            >
              {/* Outer glow */}
              <circle r={r + 8} fill={colors.ring} filter={isSel ? 'url(#demo-nodeGlowStrong)' : 'url(#demo-nodeGlow)'} />

              {/* Pulse animation */}
              {isSel && (
                <circle r={r + 12} stroke={colors.main} strokeWidth={1.5} fill="none" opacity={0.4}>
                  <animate attributeName="r" from={r + 8} to={r + 25} dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.4" to="0" dur="1.5s" repeatCount="indefinite" />
                </circle>
              )}

              {/* Main circle */}
              <circle r={r} fill="#0f1419" stroke={colors.main} strokeWidth={isSel ? 3 : 2} />
              <circle r={r - 2} fill={colors.main} opacity={0.15} />

              {/* Name */}
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={r > 22 ? 11 : 9}
                fontWeight={500}
                fill={isSel ? colors.main : '#ccd6f6'}
                style={{ pointerEvents: 'none', fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                {displayName}
              </text>

              {/* Relationship count badge */}
              {node.relCount > 0 && (
                <g transform={`translate(${r * 0.7},${-r * 0.7})`}>
                  <circle r={7} fill={colors.main} />
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={8}
                    fontWeight={600}
                    fill="#0f1419"
                    style={{ pointerEvents: 'none' }}
                  >
                    {node.relCount}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {/* Character detail popup */}
      {selectedChar && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            p: 2,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(15,20,25,0.95), rgba(13,17,23,0.95))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(136,146,176,0.15)',
            maxWidth: 280,
            zIndex: 10,
          }}
        >
          <Typography sx={{ color: '#ffd740', fontWeight: 600, fontSize: '0.9rem', mb: 0.5 }}>
            {selectedChar.name}
          </Typography>
          <Typography sx={{ color: '#8899a6', fontSize: '0.75rem', lineHeight: 1.5, mb: 1 }}>
            {selectedChar.description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {selectedChar.relationships.map((r, i) => {
              const style = getRelStyle(r.type);
              return (
                <Chip
                  key={i}
                  label={`${r.target.split(' ').pop()} ${r.type}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: `${style.color}15`,
                    color: style.color,
                    border: `1px solid ${style.color}30`,
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* Legend */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          zIndex: 10,
        }}
      >
        {[
          { label: '仲間', color: '#00e676' },
          { label: '師弟', color: '#40c4ff' },
          { label: '家族', color: '#ffab40' },
          { label: '恋愛', color: '#ff80ab' },
          { label: '敵対', color: '#ff5252' },
        ].map((l) => (
          <Chip
            key={l.label}
            label={l.label}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.55rem',
              bgcolor: `${l.color}15`,
              color: l.color,
              border: `1px solid ${l.color}30`,
            }}
          />
        ))}
      </Box>

      {/* Hint */}
      <Typography
        sx={{
          position: 'absolute',
          bottom: 12,
          right: 12,
          fontSize: '0.6rem',
          color: 'rgba(136,146,176,0.5)',
          zIndex: 10,
        }}
      >
        ノードをクリックで詳細表示 - デモデータ
      </Typography>
    </Box>
  );
};

export default DemoCharacterGraph;
